<template>
    <div class="device-history-component">
        <b-container class="full-height" fluid>
            <div class="content-container">
                <div class="settings-container bg-dark h-100 mr-3 expanded">
                    <div class="settings">
                        <b-form class="inline-form p-2">
                            <div class="inline-form-group">
                                <label for="deviceSelect">
                                    {{$t('monitoring.history.device')}}
                                </label>
                                <div class="inline-form-input">
                                    <Loader v-if="deviceLoading" />
                                    <b-form-select
                                        v-if="!deviceLoading"
                                        class="inline-form-input"
                                        id="deviceSelect"
                                        v-model="selectedDevice"
                                        :dark="true"
                                    >
                                        <b-form-select-option v-bind:key="item.id" v-for="item in deviceList" :value="item">
                                            {{item.name}}
                                        </b-form-select-option>
                                    </b-form-select>
                                </div>
                            </div>
                            <div class="inline-form-group">
                                <label for="pathColor">
                                    {{$t('monitoring.history.path_color_type')}}
                                </label>
                                <div class="inline-form-input">
                                    <b-form-select
                                        class="inline-form-input"
                                        id="pathColor"
                                        v-model="pathColor"
                                        :dark="true"
                                    >
                                        <b-form-select-option :value="0">{{$t('monitoring.history.path_color_type.by_ride')}}</b-form-select-option>
                                        <b-form-select-option :value="1">{{$t('monitoring.history.path_color_type.monotone')}}</b-form-select-option>
<!--                                        <b-form-select-option :value="2">{{$t('monitoring.history.path_color_type.by_speed')}}</b-form-select-option>-->
                                    </b-form-select>
                                </div>
                            </div>
                            <div class="inline-form-group">
                                <label for="lineHeight">
                                    {{$t('monitoring.history.line_height')}}
                                </label>
                                <div class="inline-form-input">
                                    <b-form-input
                                        id="lineHeight"
                                        v-model="lineWeight"
                                        type="number"
                                        min="1"
                                        max="10"
                                    />
                                </div>
                            </div>
                            <div class="inline-form-group">
                                <div class="inline-form-input">
                                    <b-button
                                        class="flex-grow-1"
                                        variant="outline-secondary"
                                        @click="buildTrackForToday"
                                    >
                                        {{$t('monitoring.history.today')}}
                                    </b-button>
                                    <b-button
                                        class="flex-grow-1"
                                        variant="outline-secondary"
                                        @click="buildTrackForYesterday"
                                    >
                                        {{$t('monitoring.history.yesterday')}}
                                    </b-button>
                                    <b-button
                                        class="flex-grow-1"
                                        variant="outline-secondary"
                                        @click="buildTrackForWeek"
                                    >
                                        {{$t('monitoring.history.week')}}
                                    </b-button>
                                    <b-button
                                        class="flex-grow-1"
                                        variant="outline-secondary"
                                        @click="buildTrackForMonth"
                                    >
                                        {{$t('monitoring.history.month')}}
                                    </b-button>
                                </div>
                            </div>
                            <div class="inline-form-group">
                                <label for="intervalSelect">
                                    {{$t('monitoring.history.interval')}}
                                </label>
                                <div class="inline-form-input">
                                    <b-form-select
                                        class="inline-form-input"
                                        id="intervalSelect"
                                        :dark="true"
                                        v-model="selectedInterval"
                                    >
                                        <b-form-select-option :value="0">{{$t('monitoring.history.interval.selected_period')}}</b-form-select-option>
                                        <b-form-select-option :value="1">{{$t('monitoring.history.interval.from_date_to_now')}}</b-form-select-option>
                                    </b-form-select>
                                </div>
                            </div>
                            <div class="inline-form-group">
                                <label for="dateFrom">
                                    {{$t('monitoring.history.date_from')}}
                                </label>
                                <div class="inline-form-input">
                                    <b-form-datepicker
                                        class="form-datepicker"
                                        id="dateFrom"
                                        v-model="startDate"
                                        :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"
                                        :dark="true"
                                    />
                                    <b-form-timepicker
                                        v-model="startTime"
                                        button-variant="dark"
                                        :dark="true"
                                    />
                                </div>
                            </div>
                            <div class="inline-form-group" v-show="finishDateEnabled()">
                                <label for="dateTo">
                                    {{$t('monitoring.history.date_to')}}
                                </label>
                                <div class="inline-form-input">
                                    <b-form-datepicker
                                        class="form-datepicker"
                                        id="dateTo"
                                        v-model="endDate"
                                        :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"
                                        :dark="true"
                                    />
                                    <b-form-timepicker
                                        v-model="endTime"
                                        button-variant="dark"
                                        :dark="true"
                                    />
                                </div>
                            </div>
                            <div class="inline-form-group">
                                <div class="inline-form-input justify-content-end">
                                    <b-button
                                        variant="primary"
                                        @click="buildTrackForSelected"
                                        :disabled="trackLoading"
                                    >
                                        <b-spinner small v-if="trackLoading" />
                                        {{$t('monitoring.history.build')}}
                                    </b-button>
                                </div>
                            </div>
                        </b-form>
                        <b-alert variant="danger" :show="trackLoadError">{{$t('track_load_error')}}</b-alert>
                        <div class="track-list">
                            <TreeView
                                :data="this.trackList"
                                :item-is-expanded="itemIsExpanded"
                                :item-children="trackDetails"
                            >
                                <template scope="_">
                                    <div class="icon select-icon-stack" @click="itemSelectionToggle(_.item)">
                                        <b-iconstack>
                                            <b-icon stacked icon="square"/>
                                            <b-icon stacked icon="check" v-show="_.item.display"/>
                                        </b-iconstack>
                                    </div>
                                    <div class="track-list-row" v-if="itemIsTrack(_.item)">
                                        <div class="item-text track">
                                            <div>
                                                <span>{{_.item.device.name}}</span>
                                                <span>{{trackDistance(_.item)}}</span>
                                            </div>
                                            <div class="date-interval">
                                                <div>{{trackStartDate(_.item)}}</div>
                                                <div>{{trackEndDate(_.item)}}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="track-list-row" v-if="itemIsTrip(_.item)">
                                        <div class="item-text trip">
                                            <div class="date-interval">
                                                <div>{{tripStartDate(_.item)}}</div>
                                                <div>{{tripEndDate(_.item)}}</div>
                                            </div>
                                            <div>
                                                <span>{{tripDistance(_.item)}}</span>
                                            </div>
                                            <div class="trip-color" v-bind:style="{backgroundColor: _.item.color}" />
                                        </div>
                                    </div>
                                    <ul class="item-actions">
                                        <li
                                            v-for="action in trackListActions"
                                            v-show="action.isVisible(_.item)"
                                            v-bind:key="action.id"
                                            :id="`action-${_.item.id}-${action.id}`"
                                        >
                                            <b-icon
                                                :icon="action.icon"
                                                class="action"
                                                v-bind:class="{active: action.isActive(_.item)}"
                                                @click="action.itemClicked(_.item)"
                                            />
                                            <b-tooltip
                                                :target="`action-${_.item.id}-${action.id}`"
                                                triggers="hover"
                                                placement="bottom"
                                                :delay="{show: 100, hide: 100}"
                                            >
                                                {{action.text}}
                                            </b-tooltip>
                                        </li>
                                    </ul>
                                </template>
                            </TreeView>
                        </div>
                    </div>
                </div>
                <div class="map-container">
                    <l-map
                        :zoom="map.zoom"
                        :center="map.center"
                        :options="map.mapOptions"
                        :bounds="map.bounds"
                        style="height: 100%"
                        @update:center="centerUpdate"
                        @update:zoom="zoomUpdate"
                        ref="map"
                    >
                        <l-tile-layer
                            :url="map.url"
                            :attribution="map.attribution"
                        />
                        <l-polyline
                            v-for="trip in createPathList()"
                            v-bind:key="trip.id"
                            :lat-lngs="trip.path.map(q => q.location)"
                            :color="trip.color"
                            :weight="trip.weight"
                            :visible="trip.display"
                        />
                        <l-marker
                            v-for="point in stopPoints"
                            :key="point.id"
                            :lat-lng="point.location"
                        >
                            <l-icon
                                :icon-size="dynamicSize"
                                :icon-anchor="dynamicAnchor"
                                icon-url="assets/images/stop.png" >
                            </l-icon>
                        </l-marker>
                        <l-marker
                            v-for="point in parkingPoints"
                            :key="point.id"
                            :lat-lng="point.location"
                        >
                            <l-icon
                                :icon-size="dynamicSize"
                                :icon-anchor="dynamicAnchor"
                                icon-url="assets/images/parking.png" >
                            </l-icon>
                        </l-marker>
<!--                        <div-->
<!--                            v-for="trip in createPathList()"-->
<!--                            :key="`trip-point-container-${trip.id}`"-->
<!--                        >-->
<!--                            <l-circle-->
<!--                                v-for="point in trip.path"-->
<!--                                :key="`trip-${trip.id}-point-${point.id}`"-->
<!--                                :lat-lng="point.location"-->
<!--                                :color="trip.color"-->
<!--                                :radius="dotRadius"-->
<!--                                :visible="map.zoom > 13"-->
<!--                                @click="displayTripPointDetails(trip, point)"-->
<!--                            />-->
<!--                        </div>-->
                    </l-map>
                </div>
            </div>
        </b-container>
    </div>
</template>

<script lang="ts">

import Component from "vue-class-component";
import Vue from "vue";
import {hasValidCoordinates} from "../../../utils/coordinates";
import {LatLng, LatLngBounds} from "leaflet";
import {LMap, LMarker, LPolyline, LTileLayer, LIcon, LCircle} from "vue2-leaflet";
import DeviceRepository from "../../../Repository/Device/DeviceRepository";
import Loader from "../../Common/Loader.vue";
import {DateTimeFormatter} from "../../../utils/DateTimeFormatter";
import TreeView, {ITreeItemAction} from "../../Common/TreeView.vue";
import {IDeviceRecordDto} from "../../../Repository/Device/Dto/IDevicePacketsDto";
import DeviceGroupRepository from "../../../Repository/DeviceGroup/DeviceGroupRepository";

interface IMap {
    url: string,
    attribution: string,
    zoom: number,
    center: LatLng,
    bounds: LatLngBounds | null,
    mapOptions: any,
}

interface IDevice {
    id: number;
    name: string;
}

interface IDeviceMovementDetectionConfiguration {
    minimalSpeed: number,
    minimalParkingTime: number,
    minimalSatellitesCount: number,
    maximalDistance: number,
    minimalRidingTime: number,
    minimalRidingDistance: number,
}

enum PathColorType {
    BY_RIDE = 0,
    MONOTONE = 1,
    BY_SPEED = 2
}

enum Interval {
    SELECTED_PERIOD = 0,
    FROM_DATE_TO_NOW = 1,
}

enum TreeItemType {
    TRACK,
    TRIP,
}

interface ITreeItem {
    display: boolean,
    items: ITreeItem[],
    type: TreeItemType,
}

interface ITrack extends ITreeItem{
    id: number,
    items: ITrip[],
    color: string,
    device: IDevice,
    startDate: Date,
    endDate: Date,
    weight: number,
}

interface ITrip extends ITreeItem {
    id: string,
    path: {
        id: string,
        location: LatLng,
        source: IDeviceRecordDto | null,
    }[],
    startDate: Date|null,
    endDate: Date|null,
    weight: number,
    color: string,
    distance: number,
}

const PALETTE = [
    '#ff0000',
    '#16cc19',
    '#3252a8',
    '#862fb5',
    '#a66219',
    '#3eedca',
];

const PALETTE_SINGLE_COLOR = 2;

@Component({
    components: {TreeView, LMap, LTileLayer, LMarker, Loader, LPolyline, LIcon, LCircle }
})
class DeviceHistory extends Vue {
    map: IMap = {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '',
        zoom: 13,
        center: new LatLng(55.7556956, 37.6151692),
        mapOptions: {},
        bounds: null,
    };

    showSidebar: boolean = true;
    selectedDevice: IDevice | null = null;
    deviceList: IDevice[] = [];
    deviceLoading: boolean = true;
    startDate: Date|string = new Date();
    startTime: string = '00:00:00';
    endDate: Date|string = new Date();
    endTime: string = '23:59:59';
    pathColor: PathColorType = PathColorType.BY_RIDE;
    lineWeight: number = 2;
    selectedInterval: Interval = Interval.SELECTED_PERIOD;
    trackLoading: boolean = false;
    trackLoadError: boolean = false;
    trackList: ITrack[] = [];
    trackIdCounter: number = 0;
    movementDetectionConfiguration: IDeviceMovementDetectionConfiguration|undefined;
    stopPoints: {id: string, location: LatLng}[] = [];
    parkingPoints: {id: string, location: LatLng}[] = [];

    mounted() {
        this.loadDeviceList();
    }

    created() {
        this.movementDetectionConfiguration = {
            minimalSpeed: 1,
            minimalParkingTime: 300000,
            minimalSatellitesCount: 2,
            maximalDistance: 10000,
            minimalRidingTime: 60,
            minimalRidingDistance: 100,
        };
        this.trackList = [];
    }

    get dotRadius(): number {
        return 1;
    }

    get dynamicSize(): [number, number] {
        return [16, 18.5];
    }

    get dynamicAnchor(): [number, number] {
        return [8, 18.5];
    }

    get trackListActions(): ITreeItemAction[] {
        return [
            {
                id: 1,
                icon: 'x',
                text: this.$t('monitoring.history.remove_track').toString(),
                isActive: () => false,
                isVisible: (item: ITreeItem) => item.type === TreeItemType.TRACK,
                itemClicked: (item: ITreeItem) => {
                    this.trackList?.splice(
                        this.trackList.findIndex(q => q === item),
                        1
                    );
                },
            }
        ];
    }

    centerUpdate(value: any): void {
        this.map.center = value;
    }

    zoomUpdate(value: number): void {
        this.map.zoom = value;
    }

    toggleSettings(): void {
        this.showSidebar = !this.showSidebar;
    }

    loadDeviceList() {
        this.deviceLoading = true;
        const that = this;
        DeviceGroupRepository
            .getAllDevicesWithGroups(this.$store)
            .then(response => {
                    this.deviceList = response.map(q => {
                        return {
                            id: q.id,
                            name: q.name ?? q.id,
                        };
                    });
                    this.deviceLoading = false;

                    if (this.deviceList?.length) {
                        let device: IDevice|undefined;
                        if (that.$route.params.deviceId) {
                            const needleId = Number.parseInt(that.$route.params.deviceId);
                            device = that.deviceList.find(q => q.id === needleId);
                        }
                        this.selectedDevice = device ?? this.deviceList[0];
                    }
            });
    }

    finishDateEnabled(): boolean {
        return this.selectedInterval !== Interval.FROM_DATE_TO_NOW;
    }

    buildTrack(startDate: Date|number, startTime: string, endDate: Date|number, endTime: string): void {
        if (!this.selectedDevice || this.trackLoading) {
            return;
        }

        const createDate = (date: Date|number, time: string): number => {
            let hours: string, minutes: string, seconds: string;
            [ hours, minutes, seconds ] = time.split(':');
            return new Date(date).setHours(Number.parseInt(hours), Number.parseInt(minutes), Number.parseInt(seconds ?? '0'));
        };

        const fromDate = createDate(startDate, startTime);
        const toDate = createDate(endDate, endTime);

        const track: ITrack = Object.assign({
            id: this.trackIdCounter++,
            device: Object.assign({}, this.selectedDevice),
            color: 'red',
            startDate: new Date(fromDate),
            endDate: new Date(toDate),
            weight: this.lineWeight,
            display: true,
            type: TreeItemType.TRACK,
            items: [],
        });

        this.trackLoadError = false;
        this.trackLoading = true;
        DeviceRepository
            .getPacketsCoordinatesByDeviceId(
                this.selectedDevice.id,
                fromDate,
                toDate
            )
            .then(response => {

                const topLeftCorner = new LatLng(Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER);
                const bottomRightCorner = new LatLng(Number.MIN_SAFE_INTEGER, Number.MIN_SAFE_INTEGER);
                let colorIdx = 0;
                let pathIdx = 0;
                let pointIdx = 0;
                const getNextColor = (): string => {
                    switch (this.pathColor) {
                        case PathColorType.BY_SPEED:
                        case PathColorType.BY_RIDE: return PALETTE[colorIdx++ % PALETTE.length];
                        case PathColorType.MONOTONE: return PALETTE[PALETTE_SINGLE_COLOR];
                        default: throw new Error("Unknown path color type: " + this.pathColor);
                    }
                };
                const createTrip = (): ITrip => {
                    pathIdx = 0;
                    pointIdx = 0;
                    return {
                        id: `${track.id}.${track.items.length}`,
                        startDate: null,
                        endDate: null,
                        display: true,
                        weight: track.weight,
                        color: getNextColor(),
                        type: TreeItemType.TRIP,
                        distance: 0,
                        path: [],
                        items: [],
                    };
                };
                let trip: ITrip = createTrip();
                let lastLocation: {distance: number, location: LatLng|null, timestamp: number, date: Date, speed: number} = {
                    distance: 0,
                    location: null,
                    timestamp: 0,
                    date: new Date(),
                    speed: 0,
                };

                const records = response?.records?.filter(q =>
                    hasValidCoordinates(q.coordinates)
                        && q.coordinates?.satellites >= (this.movementDetectionConfiguration?.minimalSatellitesCount ?? 2)
                ) ?? [];

                const isStopping = (speed: number) => speed < (this.movementDetectionConfiguration?.minimalSpeed ?? 1);

                records?.forEach(item => {
                    const intLat = Number(item.coordinates.latitude);
                    const intLng = Number(item.coordinates.longitude);
                    const location = new LatLng(intLat, intLng);
                    const date = new Date(item.coordinates.date);
                    const timestamp = date.getTime();
                    const distance = lastLocation.location ? lastLocation.location.distanceTo(location) : 0;

                    topLeftCorner.lat = Math.min(intLat, topLeftCorner.lat);
                    topLeftCorner.lng = Math.min(intLng, topLeftCorner.lng);
                    bottomRightCorner.lat = Math.max(intLat, bottomRightCorner.lat);
                    bottomRightCorner.lng = Math.max(intLng, bottomRightCorner.lng);

                    if (!trip.startDate) {
                        trip.startDate = date;
                    }
                    if (isStopping(item.coordinates.speed)) { // тачка стоит
                        if (isStopping(lastLocation.speed)) {
                            // trip.distance += distance;
                            return;
                        }
                    } else { // тачка едет
                        if (isStopping(lastLocation.speed)) { // до этого стояла
                            if (timestamp - lastLocation.timestamp >= (this.movementDetectionConfiguration?.minimalParkingTime ?? 300000)) {
                                // добавить предыдущую точку в точку парковки
                                trip.endDate = lastLocation.date;

                                if (trip.distance > (this.movementDetectionConfiguration?.minimalRidingDistance ?? 100)) {
                                    this.parkingPoints.push({
                                        id: `parking-${track.id}-${trip.id}-${pointIdx++},`,
                                        location: lastLocation.location ?? new LatLng(0, 0)
                                    });
                                    track.items.push(trip);
                                }
                                trip = createTrip();
                                if (lastLocation?.location) {
                                    trip.path.push({
                                        id: `${trip.id}-${pathIdx++}`,
                                        location: lastLocation.location,
                                        source: item,
                                    });
                                }
                            } else {
                                // добавить предыдущую точку в точку остановки
                                this.stopPoints.push({
                                    id: `stop-${track.id}-${trip.id}-${pointIdx++},`,
                                    location: lastLocation.location ?? new LatLng(0, 0)
                                });
                            }
                        }
                    }

                    trip.path.push({
                        id: `${trip.id}-${pathIdx++}`,
                        location: location,
                        source: item,
                    });
                    trip.distance += distance;
                    lastLocation = {
                        location: location,
                        distance: distance,
                        timestamp: timestamp,
                        date: date,
                        speed: item.coordinates.speed,
                    };
                });

                if (records.length) {
                    trip.endDate = new Date(Date.parse(records[records?.length - 1].date));
                    track.items.push(trip);
                }

                if (topLeftCorner.lat < Number.MAX_SAFE_INTEGER) {
                    this.map.bounds = new LatLngBounds(topLeftCorner, bottomRightCorner);
                }

                this.trackList?.push(track);
            })
            .catch(() => { this.trackLoadError = true; })
            .finally(() => { this.trackLoading = false; });
    }

    trackDetails(item: ITreeItem): any[] {
        return item.items;
    }

    itemIsSelectable(): boolean {
        return true;
    }

    itemIsSelected(item: ITreeItem): boolean {
        return item.display;
    }

    itemSelectionToggle(item: ITreeItem): void {
        item.display = !item.display;
        item.items.forEach(q => q.display = item.display);
    }

    itemIsExpanded(): boolean {
        return false;
    }

    createPathList(): ITrip[] {
        let result: ITrip[] = [];
        this.trackList?.forEach(q => result = result.concat(q.items));
        return result;
    }

    itemIsTrack(item: ITreeItem): boolean {
        return item.type === TreeItemType.TRACK;
    }

    itemIsTrip(item: ITreeItem): boolean {
        return item.type === TreeItemType.TRIP;
    }

    tripStartDate(trip: ITrip): string {
        return trip.startDate ? DateTimeFormatter.toYYYYMMDDHHMMSS(trip.startDate) : '';
    }

    tripEndDate(trip: ITrip): string {
        return trip.endDate ? DateTimeFormatter.toYYYYMMDDHHMMSS(trip.endDate) : '';
    }

    trackStartDate(track: ITrack): string {
        return DateTimeFormatter.toYYYYMMDDHHMMSS(track.startDate);
    }

    trackEndDate(track: ITrack): string {
        return DateTimeFormatter.toYYYYMMDDHHMMSS(track.endDate);
    }

    tripDistance(trip: ITrip): string {
        const s = (Math.round(trip.distance * 0.1) * 0.01).toString();
        return s.substr(0, s.indexOf('.') + 3) + ` ${this.$t('monitoring.history.measuring.distance')}`;
    }

    trackDistance(track: ITrack): string {
        let distance = 0;
        track.items.forEach(q => distance += q.distance);
        const s = (Math.round(distance * 0.1) * 0.01).toString();
        return s.substr(0, s.indexOf('.') + 3) + ` ${this.$t('monitoring.history.measuring.distance')}`;
    }

    buildTrackForSelected(): void {
        const now = new Date();
        const endDate = this.selectedInterval === Interval.SELECTED_PERIOD ? this.endDate : now;
        const endTime: string =
            this.selectedInterval === Interval.SELECTED_PERIOD
                ? this.endTime
                : `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`;

        this.buildTrack(new Date(this.startDate), this.startTime, new Date(endDate), endTime);
    }

    buildTrackForToday(): void {
        const now = new Date();
        this.buildTrack(now, '00:00:00', now, '23:59:59');
    }

    buildTrackForYesterday(): void {
        let now: Date|number = new Date();
        now = now.setDate(now.getDate() - 1);
        this.buildTrack(now, '00:00:00', now, '23:59:59');
    }

    buildTrackForWeek(): void {
        let now: Date|number = new Date();
        this.buildTrack(
            new Date().setDate(now.getDate() - 6 - now.getDay()), '00:00:00',
            new Date().setDate(now.getDate() - now.getDay()), '23:59:59'
        );
    }

    buildTrackForMonth(): void {
        let now: Date|number = new Date();
        this.buildTrack(
            new Date(now.getFullYear(), now.getMonth() - 1, 1), '00:00:00',
            new Date(now.getFullYear(), now.getMonth(), 0), '23:59:59'
        );
    }

    displayTripPointDetails(trip: ITrip, point: { location: LatLng, source: IDeviceRecordDto }) {
        console.log(point.source);
    }
}

export default DeviceHistory;
</script>

<style lang="scss">
@import '../../../custom1';

.full-height {
    height: calc(100vh - 64px);

    .content-container {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        height: 99%;

        .settings-container {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: flex-start;
            position: relative;
            margin-left: -1em;

            &.expanded {
                flex: 0 0 25%;
                max-width: 25%;
            }

            .inline-form {
                display: flex;
                flex-direction: column;

                .inline-form-group {
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    margin-bottom: 1em;

                    label {
                        min-width: 25%;
                        margin-right: 0.5em;
                    }

                    .inline-form-input {
                        flex-grow: 1;
                        display: flex;

                        * {
                            margin-right: 0.5em;

                            &:last-child {
                                margin-right: 0;
                            }
                        }


                        .b-calendar-grid-body {
                            span {
                                color: white !important;

                                &:hover {
                                    color: $dark!important;
                                }
                            }
                        }

                        .dropdown-menu {
                            background-color: $dark;
                        }
                    }
                }
            }

            .header-bar {
                background-color: lighten($dark, 15%);
                padding: 0.25em;
                text-align: center;
                margin: 0 -1em 0 -1em;
            }

            .settings {
                padding: 0.5em;
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;

                .group {
                    display: flex;
                    flex-direction: row;
                    justify-content: space-between;

                    .label {
                        min-width: 30%;
                        display: block;
                        text-align: end;
                        margin-right: 1em;
                    }
                }

                .track-list {
                    height: calc(100% - 2.5em);
                    overflow: hidden auto;
                    color: white;

                    .track-list-row {
                        display: flex;
                        width: 100%;

                        .item-text {
                            display: flex;
                            flex-direction: column;
                            justify-content: flex-start;
                            align-items: flex-start;
                            padding: 0.25em 0;

                            &.trip {
                                justify-content: space-between;
                                flex-direction: row;
                            }

                            &.track {
                                & > * {
                                    &.date-interval {
                                        color: darkgray;
                                    }
                                    display: flex;
                                    flex-direction: row;

                                    & > * {
                                        margin-right: 1em;

                                        &:last-child {
                                            margin-right: 0;
                                        }
                                    }
                                }
                            }

                            .date-interval > * {
                                font-size: 0.95em;
                                line-height: 1em;
                            }

                            .trip-color {
                                width: 1em;
                                height: 1em;
                            }
                        }
                    }
                }
            }

            .footer {
                width: 100%;
                white-space: nowrap;
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                align-items: flex-end;
                cursor: pointer;
                padding: 1em;

                &:hover {
                    background-color: lighten($dark, 10%);
                }
            }
        }

        .map-container {
            width: 100%;
            height: 100%;

            &.collapsed {
                height: 50%;
            }
        }
    }

}

.b-calendar-grid-body {
    span {
        color: white !important;

        &:hover {
            color: $dark!important;
        }
    }
}

.dropdown-menu {
    background-color: $dark;
}



</style>